import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import StreamingToolLoader from './StreamingToolLoader';
import useStreamSilence from '../../hooks/useStreamSilence';
import useRotatingStatus from '../../hooks/useRotatingStatus';
import { decidirActividad } from '../../streaming/activityPolicy';

/**
 * Agente sintético para `useRotatingStatus`.
 *
 * El hook solo rota si hay `agentName`, y solo rota el catálogo si NO hay `currentTask`
 * — que es exactamente lo que se quiere aquí. El nombre no se pinta en ningún sitio; el
 * consumidor de la frase es `statusText`. Mismo truco que el centinela `__processing__`
 * de `TypingIndicatorWrapper`.
 *
 * Constante a nivel de módulo, y no un literal en el render, para que la identidad no
 * cambie entre renders y el intervalo del hook no se reinicie en cada uno.
 */
const AGENTE_SILENCIO = Object.freeze({ agentName: '__silencio__' });

/**
 * StreamActivityIndicator — el pie de la burbuja en streaming: qué está pasando y
 * desde cuándo.
 *
 * Existe como componente propio, y no como un bloque dentro de `ChatMessages`, por dos
 * razones:
 *
 * 1. **Aísla los temporizadores.** Aquí dentro laten el cronómetro (1 s), la rotación
 *    de frases (3-5 s) y el detector de silencio. En el padre, cada uno de esos ticks
 *    repintaría la lista de mensajes entera, incluido el markdown. Es el mismo motivo
 *    por el que existe `TypingIndicatorWrapper` (`ChatMessages.jsx`), y aquí pesa más:
 *    la burbuja en streaming se re-renderiza ya con cada delta.
 *
 * 2. **Mantiene montado el loader.** `StreamingToolLoader` se apaga con `visible`, no
 *    desmontándolo, para que su cronómetro mida el TURNO y no cada aparición (#923).
 *    Ese contrato se respeta renderizándolo siempre desde aquí.
 *
 * La decisión de QUÉ mostrar no vive en este fichero: es `decidirActividad`, una función
 * pura y probada aparte, compartida con el chat modular.
 */
function StreamActivityIndicator({ streamingMessage, liveStatusText, isStopping, onSilencioChange }) {
  const { t } = useTranslation();

  const silencioso = useStreamSilence(streamingMessage);

  const hayHerramientas =
    Array.isArray(streamingMessage?.toolCalls) && streamingMessage.toolCalls.length > 0;

  const { visible, modo } = decidirActividad({
    texto: streamingMessage?.text || '',
    rondaCerrada: Boolean(streamingMessage?.roundDone),
    hayHerramientas,
    // El estado REAL del canal lateral, nunca la frase de relleno: si aquí entrara el
    // relleno, `rondaCerrada && hayEstado` sería siempre cierta.
    hayEstado: Boolean(liveStatusText),
    silencioso,
    deteniendo: Boolean(isStopping),
  });

  // Siempre activo, aunque no estemos en silencio: así hay una frase lista en el mismo
  // render en que el silencio se enciende. Condicionarlo dejaba un frame con el
  // indicador vacío, porque el hook publica su primera frase desde un efecto. El coste
  // es un `setState` cada 3-5 s dentro de este componente memoizado — que es
  // precisamente lo que aísla.
  const fraseNeutra = useRotatingStatus(AGENTE_SILENCIO, t);

  const enSilencio = modo === 'silencio';

  // ── El cursor y este indicador no pueden estar encendidos a la vez ──
  //
  // El cursor parpadeante significa «estoy escribiendo, la siguiente letra viene ya»;
  // el silencio significa lo contrario. Y coinciden SIEMPRE que este indicador entra en
  // modo silencio, porque para que haya cursor tiene que haber texto en la ronda — que
  // es justo la condición del hueco. Durante 30 s el cursor prometía texto inminente:
  // la misma clase de afirmación comprobable y falsa que persiguen #911 y #508, solo
  // que en forma de píxel.
  //
  // Se avisa al padre en vez de subir el hook, para no meter un reloj en `ChatMessages`
  // (ver el punto 1 de la cabecera). Son DOS avisos por episodio —encender y apagar—
  // frente a un tick por segundo, que es lo que costaría la alternativa.
  const avisar = useRef(onSilencioChange);
  avisar.current = onSilencioChange;

  useEffect(() => { avisar.current?.(enSilencio); }, [enSilencio]);

  // Al desmontarse hay que dejarlo apagado: la burbuja desaparece cuando llega el
  // `agent-finish`, y si el turno acaba en pleno hueco el padre se quedaría con el
  // cursor suprimido para el turno SIGUIENTE. Quien lo enciende, lo apaga.
  useEffect(() => () => { avisar.current?.(false); }, []);

  return (
    <StreamingToolLoader
      visible={visible}
      // En silencio NO se pinta la herramienta: `toolCalls` arrastra la de la vuelta
      // anterior, ya terminada (ver `activityPolicy.js`).
      tools={enSilencio ? undefined : streamingMessage?.toolCalls}
      statusText={enSilencio ? (fraseNeutra || undefined) : (liveStatusText || undefined)}
    />
  );
}

StreamActivityIndicator.propTypes = {
  /** El buffer vivo (`useStreamingBuffer`). `null` cuando no hay turno en streaming. */
  streamingMessage: PropTypes.shape({
    text: PropTypes.string,
    preToolText: PropTypes.string,
    toolCalls: PropTypes.array,
    roundDone: PropTypes.bool,
  }),
  /** `agentStatus.currentTask` — progreso real emitido por el subworkflow. */
  liveStatusText: PropTypes.string,
  /** El usuario pulsó ⏹ y aún no llegó la confirmación del backend. */
  isStopping: PropTypes.bool,
  /**
   * Se llama con `true` al entrar en silencio y con `false` al salir (y al desmontar).
   * El consumidor lo usa para apagar el cursor, que afirma lo contrario. Debe ser una
   * referencia estable o un `setState`: se guarda en una ref, así que cambiarlo en cada
   * render no re-dispara el aviso, pero tampoco hace falta memoizarlo.
   */
  onSilencioChange: PropTypes.func,
};

export default React.memo(StreamActivityIndicator);
